import React, {useEffect} from 'react';
import {createPortal} from 'react-dom';

import {ModalReact, ModalType, VerticalAlignment} from 'common/components/modal';
import {ButtonReact} from 'common/components/button';
import {ButtonTheme, ButtonType} from 'common/components/button/constants';
import {useTours} from 'common/contexts/react/tours-context';
import {useTranslation} from 'common/i18n/hooks';
import {usePrevious} from 'common/hooks/usePrevious';
import {Visibility} from 'common/components/base/stackable';
import {Directions, PositioningStrategy} from 'common/components/drop-menu/types';

import {DropMenuReact} from '../drop-menu';
import {MarkdownReact} from '../markdown';
import styles from './index.module.styl';

function setHighlightOutline(element: HTMLElement | null | undefined, on: boolean = false) {
  if (!element?.isConnected) return;

  element.classList.toggle(`tour-pulse`, on);
}

export function ProductTour() {
  const {t, print} = useTranslation();
  const tours = useTours();
  const previousTargetEl = usePrevious(tours.activeTargetEl);
  const tour = tours.activeTour;

  /** adds a highlight border to the target element of the tour */
  useEffect(() => {
    setHighlightOutline(previousTargetEl, false);
    setHighlightOutline(tours.activeTargetEl, true);

    return () => {
      setHighlightOutline(previousTargetEl, false);
      setHighlightOutline(tours.activeTargetEl, false);
    };
  }, [tours.activeTargetEl]);

  if (tour === null) {
    return null;
  }

  const step = tour.stepData[tours.activeTourStep];
  const stepIndex = tours.activeTourStep - (tour.includeFirstStepInStepCount === false ? 0 : 1) + 1;
  const totalSteps = tour.stepData.length - (tour.includeFirstStepInStepCount === false ? 0 : 1);

  const handleLinkClick = (type: `primary` | `secondary`) => {
    const linkText = print(type === `primary` ? step.primaryButtonText! : step.secondaryButtonText!);
    const linkUrl = type === `primary` ? step.primaryButtonLink : step.secondaryButtonLink;

    mixpanel.track(`Tour Modal Clicked`, {
      'tour id': tours.activeTourName,
      linkText,
      linkUrl,
    });

    if (type === `primary`) {
      tours.nextStep();
      step.onClickPrimaryButton?.();
    } else if (type === `secondary`) {
      tours.prevStep();
    }
  };

  const tourBody = (
    <div className={styles.tourContainer} style={{maxWidth: tour.maxWidth}}>
      {step.tourImage && <img className={styles.image} src={step.tourImage} alt='Tour Modal Image' />}
      <div className={styles.body}>
        <div className={styles.title}>
          <MarkdownReact multiLine text={print(step.title)} />
        </div>
        <div className={styles.description}>
          <MarkdownReact multiLine text={print(step.description)} />
        </div>
        {step.secondaryDescription && <p className={styles.secondaryDescription}>{print(step.secondaryDescription)}</p>}
        <div className={styles.tourButtons}>
          <div className={styles.leftButtons}>
            {step.secondaryButtonText && (
              <ButtonReact
                className={styles.secondaryButton}
                theme={ButtonTheme.Tertiary}
                type={ButtonType.Info}
                notFocusable
                onClick={() => handleLinkClick(`secondary`)}
                href={step.secondaryButtonLink}
                target='_blank'
              >
                {print(step.secondaryButtonText)}
              </ButtonReact>
            )}
          </div>
          {(tours.activeTourStep > 0 || tour.includeFirstStepInStepCount) && (
            <div className={styles.stepCounter}>
              {stepIndex} of {totalSteps}
            </div>
          )}
          <div className={styles.rightButtons}>
            {step.displayDismissButton && (
              <ButtonReact
                className={styles.dismissButton}
                theme={ButtonTheme.Tertiary}
                type={ButtonType.Info}
                notFocusable
                onClick={() => tours.dismissTour()}
              >
                {t(`common.dismiss`)}
              </ButtonReact>
            )}
            {step.primaryButtonText && (
              <ButtonReact
                className={styles.primaryButton}
                theme={ButtonTheme.Primary}
                notFocusable
                onClick={() => handleLinkClick(`primary`)}
                href={step.primaryButtonLink}
                target='_blank'
              >
                {print(step.primaryButtonText)}
              </ButtonReact>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  if (tours.activeTargetEl) {
    return createPortal(
      <DropMenuReact
        open
        disableAutoFocus
        preventClose
        preventStacking
        positionStrategy={PositioningStrategy.Fixed}
        positionParent={tours.activeTargetEl}
        direction={Directions.DownCenter}
        // @ts-ignore
        onChange={(ev) => ev.detail.state === Visibility.Closed && tours.dismissTour({persist: false})}
      >
        {tourBody}
      </DropMenuReact>,
      window.document.querySelector(`#tooltips`) || document.body,
    );
  }

  return (
    <ModalReact
      open
      modalTitle={print(step.title)}
      modalDescription={print(step.description)}
      growToContent
      verticalAlignment={VerticalAlignment.Bottom}
      notFullscreen
      modalType={ModalType.TourModal}
      hideCloseButton
    >
      {tourBody}
    </ModalReact>
  );
}
